import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Gift, RotateCcw, AlertCircle, DollarSign, Clock, Sparkles, Camera, KeyRound, Eye, Unlock, Loader2 } from "lucide-react";
import { unlockAudio, cueSuccess, cueRedeem, cueError, cueDuplicate } from "../lib/scanner_audio";
import { PinPad, PIN_MESSAGES } from "../components/PinPad";
import { CustomerPinCard } from "../components/CustomerPinCard";

/**
 * `/stamp/:token` — the URL every wallet-pass QR encodes.
 * Staff point their phone camera at the pass → this page opens → the FIRST time
 * on that phone they type the business's 4-digit staff PIN, which unlocks the
 * phone for 12 hours → every scan after that records instantly (stamp / visit /
 * points / reward) and the customer's wallet pass updates live.
 */

const BACKEND = process.env.REACT_APP_BACKEND_URL;

async function postJson(path, body) {
  const r = await fetch(`${BACKEND}/api${path}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body || {}) });
  const data = await r.json().catch(() => ({}));
  return { ok: r.ok, status: r.status, data };
}

// 12-hour staff unlock, remembered per phone + business (server-signed, fixed expiry).
const unlockKey = (orgId) => `bp_staff_unlock:${orgId}`;
function loadUnlock(orgId) {
  try {
    const s = JSON.parse(localStorage.getItem(unlockKey(orgId)) || "null");
    if (s?.token && new Date(s.expires_at).getTime() > Date.now() + 5000) return s;
  } catch { /* corrupt → treat as locked */ }
  localStorage.removeItem(unlockKey(orgId));
  return null;
}
// Every live unlock on this phone — a staff phone almost always has exactly one (its own shop),
// which lets us fire the stamp before we even know which business the pass belongs to.
function liveUnlocks() {
  const out = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith("bp_staff_unlock:")) { const s = loadUnlock(k.slice("bp_staff_unlock:".length)); if (s) out.push({ orgId: k.slice("bp_staff_unlock:".length), ...s }); }
    }
  } catch { /* storage unavailable */ }
  return out;
}
const saveUnlock = (orgId, s) => { try { localStorage.setItem(unlockKey(orgId), JSON.stringify(s)); } catch { /* private mode */ } };
const clearUnlock = (orgId) => { try { localStorage.removeItem(unlockKey(orgId)); } catch { /* ignore */ } };
const untilLabel = (iso) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

const typeLabel = (t) => (t === "points" ? "points" : t === "visit" || t === "membership" ? "visits" : "stamps");
const brandOf = (org) => ({ bg: org?.brand?.brand_color || "#0B0D10", accent: org?.brand?.secondary_color || "#C8FF4D" });

const STAFF_PIN_MESSAGES = {
  staff_pin_incorrect: "That's not the staff PIN. Try again.",
  staff_pin_locked: "Too many wrong attempts on this phone. Try again in 5 minutes.",
  staff_pin_required: "Enter the staff PIN.",
};
const UNLOCK_ENDED = "This phone's 12-hour unlock has ended. Enter the PIN once to unlock it again.";

// One early stamp request per pass token, shared across remounts (React StrictMode mounts
// effects twice in dev; a double-fire would read as a duplicate scan).
const earlyStamps = new Map();
function earlyStamp(token, sessionToken) {
  if (!earlyStamps.has(token)) {
    const p = postJson(`/stamp/${token}`, { staff_session: sessionToken }).finally(() => setTimeout(() => earlyStamps.delete(token), 5000));
    earlyStamps.set(token, p);
  }
  return earlyStamps.get(token);
}

function Shell({ org, children, testid, center = true }) {
  const { bg, accent } = brandOf(org);
  return (
    <div className="fixed inset-0 flex flex-col text-white overflow-y-auto" style={{ background: bg }} data-testid={testid}>
      <div className="absolute inset-0 grain opacity-25 pointer-events-none" />
      <div className="absolute -top-16 -right-16 h-[380px] w-[380px] rounded-full blur-3xl opacity-[0.14] pointer-events-none" style={{ background: accent }} />
      <div className={`relative z-10 flex-1 flex flex-col px-6 pt-10 pb-[max(1.5rem,env(safe-area-inset-bottom))] max-w-md mx-auto w-full ${center ? "justify-center" : ""}`}>{children}</div>
    </div>
  );
}

function Footer() {
  return <div className="text-center text-white/35 text-[11px] pt-5">Powered by BackPocket</div>;
}

function BalanceCard({ holder, program, accent, bg, compact }) {
  const threshold = program?.reward?.threshold || 10;
  const balance = holder?.balance || 0;
  const ready = holder?.reward_status === "available";
  return (
    <div className={`rounded-3xl bg-white/[0.06] border border-white/12 backdrop-blur-md text-center ${compact ? "px-5 py-4" : "px-8 py-6"}`}>
      <div className="text-[11px] uppercase tracking-[0.18em] text-white/50 font-semibold">{holder?.staff_note || holder?.pass_number || "Pass"}</div>
      <div className={`font-display font-extrabold leading-none mt-2 ${compact ? "text-[34px]" : "text-5xl"}`} data-testid="pass-balance">
        {balance}<span className="text-white/40 text-2xl">/{threshold}</span>
      </div>
      <div className="text-[11px] uppercase tracking-[0.2em] text-white/50 mt-2">{typeLabel(program?.type)}</div>
      {ready ? (
        <div className="mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-semibold" style={{ background: accent, color: bg }}><Gift size={13} />{program?.reward?.name || "Reward"} ready</div>
      ) : (
        <div className="mt-3 text-[12px] text-white/50">{Math.max(0, threshold - balance)} more to {program?.reward?.name || "the reward"}</div>
      )}
    </div>
  );
}

function StaffPinScreen({ ctx, onSubmit, error, submitting, onViewer }) {
  const { bg, accent } = brandOf(ctx?.org);
  const ready = ctx?.holder?.reward_status === "available";
  const type = ctx?.program?.type;
  const willDo = ready ? `Redeem ${ctx?.program?.reward?.name || "reward"}` : type === "points" ? "Add points" : type === "stamp" ? "Add a stamp" : "Record a visit";
  return (
    <Shell org={ctx?.org} testid="staff-pin-screen">
      <div className="text-[11px] uppercase tracking-[0.24em] text-white/60 font-semibold text-center">{ctx?.org?.name || "Loyalty"}</div>
      <div className="mt-4"><BalanceCard holder={ctx?.holder} program={ctx?.program} accent={accent} bg={bg} compact /></div>
      <div className="mt-7 text-center">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold" style={{ color: accent }}><KeyRound size={13} /> Staff PIN</div>
        <div className="text-white/60 text-[13.5px] mt-1.5" data-testid="staff-pin-action">Enter the PIN to <span className="text-white font-semibold">{willDo.toLowerCase()}</span>.</div>
        <div className="text-white/40 text-[12px] mt-1" data-testid="staff-pin-unlock-hint">Unlocks this phone for 12 hours — no PIN on the next scans.</div>
      </div>
      <div className="mt-5"><PinPad onComplete={onSubmit} error={error} disabled={submitting} accent={accent} ink={bg} testid="staff-pin" /></div>
      <button type="button" onClick={onViewer} data-testid="staff-pin-viewer" className="mt-6 inline-flex items-center justify-center gap-1.5 text-white/50 hover:text-white text-[12.5px] transition"><Eye size={13} /> Not staff? Just view this pass</button>
      <Footer />
    </Shell>
  );
}

function WorkingScreen({ ctx }) {
  const { accent } = brandOf(ctx?.org);
  return (
    <Shell org={ctx?.org} testid="working-screen">
      <div className="text-center">
        <Loader2 size={34} className="mx-auto animate-spin" style={{ color: accent }} />
        <div className="text-[11px] uppercase tracking-[0.24em] text-white/60 font-semibold mt-6">{ctx?.org?.name || "Loyalty"}</div>
        <div className="font-display font-extrabold tracking-[-0.02em] mt-2" style={{ fontSize: "clamp(1.6rem, 6vw, 2.2rem)" }}>Recording…</div>
      </div>
    </Shell>
  );
}

function UnlockChip({ unlock, onLock }) {
  if (!unlock) return null;
  return (
    <div data-testid="staff-unlock-chip" className="fixed z-50 right-3 flex items-center gap-1.5 rounded-full bg-black/35 backdrop-blur-md border border-white/15 text-white/80 text-[11px] font-medium pl-2.5 pr-1 h-7" style={{ top: "max(env(safe-area-inset-top), 10px)" }}>
      <Unlock size={11} /> <span data-testid="staff-unlock-until">Staff until {untilLabel(unlock.expires_at)}</span>
      <button type="button" onClick={onLock} data-testid="staff-unlock-lock" className="ml-1 h-5 px-2 rounded-full bg-white/15 hover:bg-white/25 text-[10.5px] font-semibold transition-colors">Lock</button>
    </div>
  );
}

function ViewerScreen({ ctx, onStaff }) {
  const { bg, accent } = brandOf(ctx?.org);
  return (
    <Shell org={ctx?.org} testid="viewer-screen">
      <div className="text-center">
        <div className="text-[11px] uppercase tracking-[0.24em] text-white/60 font-semibold mb-2">{ctx?.org?.name || "Loyalty"}</div>
        <div className="font-display font-extrabold tracking-[-0.02em] leading-[0.95]" style={{ fontSize: "clamp(2rem, 7vw, 2.6rem)" }}>{ctx?.program?.name || "Loyalty pass"}</div>
        <div className="text-white/60 text-[14px] mt-3">Show your pass to staff to earn a {typeLabel(ctx?.program?.type).slice(0, -1)}.</div>
      </div>
      <div className="mt-8"><BalanceCard holder={ctx?.holder} program={ctx?.program} accent={accent} bg={bg} /></div>
      {ctx?.program?.redeem_pin_required && <CustomerPinCard token={ctx?.token} pinSet={!!ctx?.holder?.pin_set} accent={accent} ink={bg} />}
      <button onClick={onStaff} data-testid="viewer-staff-signin" className="mt-8 mx-auto inline-flex items-center gap-2 rounded-full h-11 px-5 bg-white/[0.08] hover:bg-white/[0.15] border border-white/12 text-white/85 text-[13px] font-semibold transition"><KeyRound size={14} /> Staff? Enter PIN</button>
      <Footer />
    </Shell>
  );
}

// Is this pass actually on a phone, and did the phone pick up the change? We poll the server
// for proof the phone re-downloaded the pass — "Updated" is only shown once that is true.
// 0 devices = the customer hasn't finished adding it to Wallet (we keep polling: phones often
// register a few seconds after the first scan). "deferred" = Apple's per-phone refresh limit
// is holding the wake for a moment; the server sends it the second the phone allows it.
const DELIVERY_POLL_MS = 2000;
const DELIVERY_POLL_MAX = 12;
const DELIVERY_POLL_MAX_DEFERRED = 50;
const phonesLabel = (n) => (n === 1 ? "their phone" : `${n} phones`);
function PhoneStatus({ devices, accent, token, version }) {
  const [live, setLive] = useState({ state: devices > 0 ? "waiting" : "no_device", devices: devices || 0 });
  useEffect(() => {
    if (devices === null || devices === undefined || !token) return undefined;
    let alive = true, tries = 0, timer = null, last = devices > 0 ? "waiting" : "no_device", limit = DELIVERY_POLL_MAX;
    setLive({ state: last, devices: devices || 0 });
    const tick = async () => {
      try {
        const r = await fetch(`${BACKEND}/api/stamp/${token}/delivery`);
        const d = await r.json();
        if (!alive) return;
        if (d.wallet) setLive((s) => ({ ...s, wallet: d.wallet }));
        if (d.state === "confirmed") { setLive({ state: "confirmed", devices: d.devices, wallet: d.wallet }); return; }
        if (d.state === "deferred") limit = DELIVERY_POLL_MAX_DEFERRED;
        if (["no_device", "waiting", "deferred"].includes(d.state)) { last = d.state; setLive({ state: d.state, devices: d.devices, eta: d.eta_sec, wallet: d.wallet }); }
      } catch { /* transient — keep polling */ }
      if (!alive) return;
      if (++tries < limit) timer = setTimeout(tick, DELIVERY_POLL_MS);
      else if (last !== "no_device") setLive((s) => ({ ...s, state: "slow" }));
    };
    timer = setTimeout(tick, 1200);
    return () => { alive = false; clearTimeout(timer); };
  }, [devices, token, version]);
  if (devices === null || devices === undefined) return null;
  const google = live.wallet === "google";
  const phones = google ? "Google Wallet" : phonesLabel(live.devices || 1);
  if (live.state === "confirmed") return (
    <div data-testid="stamped-phone-status" data-state="confirmed" data-wallet={live.wallet || "apple"} className="mt-3 inline-flex items-center gap-1.5 text-[11.5px] text-white/75">
      <Check size={13} strokeWidth={3} style={{ color: accent }} /> Updated {google ? "in" : "on"} {phones}
    </div>
  );
  if (live.state === "deferred") return (
    <div data-testid="stamped-phone-status" data-state="deferred" className="mt-3 inline-flex items-start gap-1.5 text-[11.5px] text-white/60 text-left max-w-[250px]">
      <Clock size={13} className="mt-[1px] shrink-0" /> Saved. Their phone was refreshed a moment ago — Apple allows the next refresh in about {Math.max(5, Math.round((live.eta || 60) / 5) * 5)}s. It will update by itself.
    </div>
  );
  if (live.state === "slow") return (
    <div data-testid="stamped-phone-status" data-state="slow" className="mt-3 inline-flex items-start gap-1.5 text-[11.5px] text-white/55 text-left max-w-[250px]">
      <Clock size={13} className="mt-[1px] shrink-0" /> Saved. Their phone hasn't picked it up yet — it will the moment it checks in. Nothing to do.
    </div>
  );
  if (live.state === "waiting") return (
    <div data-testid="stamped-phone-status" data-state="waiting" className="mt-3 inline-flex items-center gap-1.5 text-[11.5px] text-white/60">
      <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: accent }} /> Updating {phones}…
    </div>
  );
  return (
    <div data-testid="stamped-phone-status" data-state="no_device" data-wallet={live.wallet || "apple"} className="mt-3 inline-flex items-start gap-1.5 text-[11.5px] text-amber-200/90 text-left max-w-[240px]">
      <AlertCircle size={13} className="mt-[1px] shrink-0" /> Saved — but this pass isn't {google ? "in Google Wallet" : "on a phone"} yet. Ask them to add it to {google ? "Google Wallet" : "Wallet"}.
    </div>
  );
}

function StampedScreen({ result, org, token, onUndo, undoing, onDone, onAddAnother, adding }) {
  const { bg, accent } = brandOf(org);
  const holder = result.holder || {};
  const program = result.program || {};
  const threshold = program.reward?.threshold || 10;
  const isRedeem = result.is_redeem;
  const rewardName = program.reward?.name || "Reward";
  const unit = typeLabel(program.type).slice(0, -1);
  return (
    <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.35 }} className="fixed inset-0 flex flex-col text-white overflow-y-auto" style={{ background: bg }} data-testid="stamped-screen">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[520px] w-[520px] rounded-full blur-3xl opacity-25 pointer-events-none" style={{ background: accent }} />
      <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 py-10 text-center max-w-md mx-auto w-full">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.05 }} className="h-24 w-24 rounded-full grid place-items-center mb-7" style={{ background: accent, color: bg }}>
          {isRedeem ? <Gift size={44} strokeWidth={2.4} /> : <Check size={54} strokeWidth={3.2} />}
        </motion.div>
        <div className="text-[11px] uppercase tracking-[0.24em] text-white/60 font-semibold mb-2">{org?.name || "Loyalty"}</div>
        <div className="font-display font-extrabold tracking-[-0.03em] leading-[0.9] whitespace-pre-line" style={{ fontSize: "clamp(2.6rem, 10vw, 4.2rem)" }} data-testid="stamped-title">
          {isRedeem ? "Reward\nredeemed" : `+${result.change || 1} ${unit}`}
        </div>
        <div className="mt-7 rounded-3xl bg-white/[0.06] border border-white/12 backdrop-blur-md px-8 py-6 min-w-[260px]">
          <div className="text-[11px] uppercase tracking-[0.18em] text-white/50 font-semibold">{holder.staff_note || holder.pass_number}</div>
          <div className="font-display font-extrabold text-5xl mt-2 leading-none" data-testid="stamped-balance">{holder.balance || 0}<span className="text-white/40 text-2xl">/{threshold}</span></div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-white/50 mt-2">{typeLabel(program.type)}</div>
          {(isRedeem || result.reward_unlocked) && (
            <div className="mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-semibold" style={{ background: accent, color: bg }}>
              {isRedeem ? <Gift size={13} /> : <Sparkles size={13} strokeWidth={2.4} />}{isRedeem ? rewardName : `${rewardName} unlocked`}
            </div>
          )}
          <PhoneStatus devices={result.wallet_devices} accent={accent} token={token} version={`${result.idempotency_key || ""}:${holder.balance ?? ""}`} />
        </div>
        <div className="mt-6 flex items-center gap-5">
          {!isRedeem && (
            <button onClick={onAddAnother} disabled={adding} data-testid="stamped-add-another" className="text-white/70 hover:text-white text-[13px] font-semibold inline-flex items-center gap-1.5 transition disabled:opacity-40">
              {adding ? "Adding…" : program.type === "points" ? "＋ Add more points" : "＋ Add another (bought 2+)"}
            </button>
          )}
          {result.idempotency_key && (
            <button onClick={onUndo} disabled={undoing} data-testid="stamped-undo" className="text-white/70 hover:text-white text-[13px] font-semibold inline-flex items-center gap-1.5 transition disabled:opacity-40"><RotateCcw size={13} />{undoing ? "Undoing…" : "Undo"}</button>
          )}
        </div>
      </div>
      <div className="relative z-10 px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] max-w-md mx-auto w-full">
        <button onClick={onDone} data-testid="stamped-ready-next" className="w-full rounded-2xl h-14 font-semibold text-[15px] flex items-center justify-center gap-2 transition active:scale-[0.98]" style={{ background: accent, color: bg }}>
          <Camera size={17} strokeWidth={2.2} /> Done — next customer
        </button>
        <Footer />
      </div>
    </motion.div>
  );
}

function ReadyScreen({ org }) {
  return (
    <Shell org={org} testid="ready-screen">
      <div className="text-center">
        <div className="h-20 w-20 rounded-2xl bg-white/[0.08] border border-white/15 grid place-items-center mx-auto mb-6"><Camera size={30} className="text-white/85" strokeWidth={2} /></div>
        <div className="text-[11px] uppercase tracking-[0.24em] text-white/60 font-semibold mb-2">{org?.name || "Loyalty"}</div>
        <div className="font-display font-extrabold tracking-[-0.02em] leading-[0.95]" style={{ fontSize: "clamp(1.9rem, 6.5vw, 2.6rem)" }}>All done.</div>
        <div className="text-white/60 text-[14px] mt-3 max-w-[32ch] mx-auto leading-relaxed">Close this and point your camera at the next customer's pass.</div>
      </div>
      <Footer />
    </Shell>
  );
}

function CooldownScreen({ result, org, onAddAnyway, onDone, adding }) {
  const { bg, accent } = brandOf(org);
  const wait = result.wait_min || 0;
  const waitLabel = wait >= 60 ? `${Math.round(wait / 60)}h` : `${wait} min`;
  return (
    <Shell org={org} testid="cooldown-screen">
      <div className="text-center">
        <div className="h-20 w-20 rounded-full bg-white/[0.08] grid place-items-center mx-auto mb-6 border border-white/12"><Clock size={32} className="text-white/85" strokeWidth={2} /></div>
        <div className="text-[11px] uppercase tracking-[0.24em] text-white/60 font-semibold mb-2">{org?.name || "Loyalty"}</div>
        <div className="font-display font-extrabold tracking-[-0.02em] leading-[0.95]" style={{ fontSize: "clamp(1.9rem, 7vw, 2.6rem)" }}>Already recorded</div>
        <div className="text-white/60 text-[14px] mt-3 max-w-[36ch] mx-auto leading-relaxed">This pass was scanned in the last {waitLabel}. Nothing was added. Add another only for a genuine extra purchase.</div>
      </div>
      <div className="mt-8"><BalanceCard holder={result.holder} program={result.program} accent={accent} bg={bg} /></div>
      <button onClick={onAddAnyway} disabled={adding} data-testid="cooldown-add-anyway" className="mt-6 w-full rounded-2xl h-12 font-semibold text-[14px] bg-white/[0.08] border border-white/12 hover:bg-white/[0.14] transition disabled:opacity-40">{adding ? "Adding…" : "＋ Add another (bought 2+)"}</button>
      <button onClick={onDone} data-testid="cooldown-back" className="mt-3 w-full rounded-2xl h-14 font-semibold text-[15px] transition active:scale-[0.98]" style={{ background: accent, color: bg }}>Done — next customer</button>
      <Footer />
    </Shell>
  );
}

function CustomerPinScreen({ pending, org, error, onPin, onEarnInstead, onCancel, submitting }) {
  const { bg, accent } = brandOf(org);
  const notSet = pending?.reason === "pin_not_set";
  return (
    <Shell org={org} testid={notSet ? "pin-not-set" : "pin-prompt"}>
      <div className="text-center">
        <div className="text-[11px] uppercase tracking-[0.24em] text-white/60 font-semibold mb-2">{org?.name || "Loyalty"}</div>
        <div className="font-display font-extrabold tracking-[-0.02em] leading-[0.95] mb-2" style={{ fontSize: "clamp(1.8rem, 6.5vw, 2.5rem)" }}>{notSet ? "No PIN on this pass" : "Customer PIN"}</div>
        <div className="text-white/60 text-[14px] mb-6">{pending?.holder?.pass_number} · {pending?.program?.reward?.name || "Reward"} ready</div>
      </div>
      {notSet ? (
        <div className="rounded-3xl bg-white/[0.06] border border-white/12 p-5 text-[13.5px] text-white/75 leading-relaxed">This program needs the customer's 4-digit PIN to redeem. Ask them to open the link on the back of their pass and set one, then scan again.</div>
      ) : (
        <>
          <div className="text-white/70 text-[13.5px] mb-5 text-center">Hand the phone to the customer to enter their PIN.</div>
          <PinPad onComplete={onPin} error={error} disabled={submitting} accent={accent} ink={bg} testid="redeem-pin" />
        </>
      )}
      <button type="button" onClick={onEarnInstead} disabled={submitting} data-testid="pin-earn-instead" className="mt-6 w-full rounded-2xl h-12 font-semibold text-[14px] bg-white/[0.08] border border-white/12 hover:bg-white/[0.14] transition disabled:opacity-40">Skip redeeming — record a visit instead</button>
      <button type="button" onClick={onCancel} className="mt-3 w-full h-11 text-white/60 hover:text-white text-[13px] transition">Cancel</button>
    </Shell>
  );
}

function PointsPrompt({ pending, org, onSubmit, onCancel, submitting }) {
  const { bg, accent } = brandOf(org);
  const [amount, setAmount] = useState("");
  const quick = [5, 10, 15, 20, 25];
  return (
    <Shell org={org} testid="points-prompt">
      <div className="text-center">
        <div className="text-[11px] uppercase tracking-[0.24em] text-white/60 font-semibold mb-2">{org?.name || "Loyalty"}</div>
        <div className="font-display font-extrabold tracking-[-0.02em] leading-[0.95] mb-2" style={{ fontSize: "clamp(2rem, 7vw, 2.8rem)" }}>Amount spent?</div>
        <div className="text-white/60 text-[14px] mb-5">{pending.holder?.pass_number} · {pending.program?.name}</div>
      </div>
      <div className="grid grid-cols-5 gap-2 mb-4">
        {quick.map((n) => (
          <button key={n} type="button" onClick={() => setAmount(String(n))} data-testid={`points-quick-${n}`}
            className={`rounded-xl h-12 font-display font-bold text-[15px] border transition active:scale-[0.96] ${String(n) === amount ? "border-transparent" : "bg-white/[0.06] text-white/85 border-white/12 hover:bg-white/[0.12]"}`}
            style={String(n) === amount ? { background: accent, color: bg } : {}}>${n}</button>
        ))}
      </div>
      <form onSubmit={(e) => { e.preventDefault(); const n = parseFloat(amount); if (n > 0) onSubmit(n); }}>
        <div className="relative">
          <DollarSign size={20} className="absolute left-5 top-1/2 -translate-y-1/2 text-white/40" />
          <input autoFocus inputMode="decimal" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" data-testid="points-amount-input"
            className="w-full bg-white/[0.06] border border-white/15 rounded-2xl h-16 pl-12 pr-5 text-[22px] font-display font-bold text-white placeholder-white/25 focus:outline-none focus:border-white/40" />
        </div>
        <button type="submit" disabled={submitting || !amount || parseFloat(amount) <= 0} data-testid="points-submit" className="mt-4 w-full rounded-2xl h-14 font-semibold text-[15px] inline-flex items-center justify-center gap-2 transition disabled:opacity-50 active:scale-[0.98]" style={{ background: accent, color: bg }}>
          {submitting ? "Saving…" : <><Check size={17} strokeWidth={2.6} />Add ${amount || "0"} to their pass</>}
        </button>
        <button type="button" onClick={onCancel} className="mt-3 w-full h-11 text-white/60 hover:text-white text-[13px] transition">Cancel</button>
      </form>
    </Shell>
  );
}

function MessageScreen({ org, title, body, testid }) {
  return (
    <Shell org={org} testid={testid}>
      <div className="text-center">
        <div className="h-14 w-14 rounded-full bg-[#FF7A3D]/20 grid place-items-center mx-auto mb-4"><AlertCircle size={22} className="text-[#FF7A3D]" /></div>
        <div className="font-display text-2xl font-extrabold tracking-[-0.02em]">{title}</div>
        <div className="text-white/60 text-[13px] mt-2 leading-relaxed">{body}</div>
      </div>
    </Shell>
  );
}

export default function StampAction() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [state, setState] = useState({ kind: "loading" });
  const [staffPin, setStaffPin] = useState("");
  const [unlock, setUnlock] = useState(null);
  const [pinError, setPinError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [undoing, setUndoing] = useState(false);

  const loadCtx = async () => {
    const r = await fetch(`${BACKEND}/api/stamp/${token}/context`);
    if (!r.ok) return null;
    const next = { ...(await r.json()), token };
    setCtx(next);
    return next;
  };

  // Credentials for every write: the phone's 12h unlock if it has one, else the PIN just typed.
  const creds = (pin = staffPin, u = unlock) => ({ staff_session: u?.token || null, staff_pin: pin || null });

  // Server echoes/refreshes the unlock on every successful write → keep it.
  const keepUnlock = (orgId, d) => {
    if (d?.staff_session?.token) { saveUnlock(orgId, d.staff_session); setUnlock(d.staff_session); }
  };
  const dropUnlock = (orgId = ctx?.org?.id) => { if (orgId) clearUnlock(orgId); setUnlock(null); setStaffPin(""); };
  const lock = () => { dropUnlock(); setPinError(null); setState({ kind: "pin" }); };

  useEffect(() => {
    let alive = true;
    (async () => {
      // Speed: with exactly one unlocked business on this phone, fire the stamp NOW, in
      // parallel with the page context — one round-trip instead of two on every scan.
      const live = liveUnlocks();
      const early = live.length === 1 ? earlyStamp(token, live[0].token) : null;
      if (early) setState({ kind: "working" });
      const c = await loadCtx();
      if (!alive) return;
      if (!c) { setState({ kind: "not_found" }); return; }
      const u = loadUnlock(c.org?.id);
      if (!u) { setState({ kind: "pin" }); return; }
      setUnlock(u);
      setState({ kind: "working" });
      const res = await (early && live[0].orgId === c.org?.id ? early : postJson(`/stamp/${token}`, { staff_session: u.token }));
      if (!alive) return;
      if (!res.ok) { setPinError(res.data?.detail || "Something went wrong. Try again."); setState({ kind: "pin" }); return; }
      keepUnlock(c.org?.id, res.data);
      route(res.data, { autoRun: true, orgId: c.org?.id });
    })();
    return () => { alive = false; };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const route = (d, { autoRun = false, orgId } = {}) => {
    if (d.ok === false) {
      if (STAFF_PIN_MESSAGES[d.reason]) {
        dropUnlock(orgId || ctx?.org?.id);
        if (autoRun && d.reason === "staff_pin_required") { setPinError(UNLOCK_ENDED); setState({ kind: "pin" }); return; }
        cueError(); setPinError(STAFF_PIN_MESSAGES[d.reason]); setState({ kind: "pin" }); return;
      }
      if (d.reason === "needs_amount") { setState({ kind: "points_prompt", pending: d }); return; }
      if (d.reason === "blocked") { cueError(); setState({ kind: "blocked" }); return; }
      if (d.reason === "pin_required" || d.reason === "pin_not_set" || d.reason === "pin_locked") { setState({ kind: "customer_pin", pending: d, error: d.reason === "pin_locked" ? PIN_MESSAGES.pin_locked : null }); return; }
      if (d.reason === "no_reward") { cueError(); setState({ kind: "pin" }); setPinError("No reward is ready on this pass yet."); return; }
      cueError(); setPinError("Something went wrong. Try again."); setState({ kind: "pin" }); return;
    }
    if (d.duplicate) { cueDuplicate(); setState({ kind: "cooldown", result: d }); return; }
    if (d.is_redeem || d.reward_unlocked) cueRedeem(); else cueSuccess();
    setState({ kind: "stamped", result: d });
  };

  const post = async (body) => {
    unlockAudio();
    setSubmitting(true);
    try {
      const res = await postJson(`/stamp/${token}`, { ...creds(), ...body });
      if (!res.ok) { setPinError(res.data?.detail || "Something went wrong. Try again."); setState({ kind: "pin" }); return null; }
      keepUnlock(ctx?.org?.id, res.data);
      return res.data;
    } finally { setSubmitting(false); }
  };

  const submitStaffPin = async (pin) => {
    setPinError(null); setStaffPin(pin); setSubmitting(true);
    try {
      const res = await postJson(`/stamp/${token}`, { staff_pin: pin });
      if (!res.ok) { setPinError(res.data?.detail || "Something went wrong. Try again."); return; }
      keepUnlock(ctx?.org?.id, res.data);
      route(res.data);
    } finally { setSubmitting(false); }
  };

  // another=true → the server hands out the next free slot in the 4h window, so "Add another" always lands.
  const earn = async (another = false) => { const d = await post(another ? { action: "earn", another: true } : { action: "earn" }); if (d) route(d); };
  const redeemWithCustomerPin = async (customerPin) => {
    const d = await post({ action: "redeem", customer_pin: customerPin });
    if (!d) return;
    if (d.ok === false && PIN_MESSAGES[d.reason] && d.reason !== "pin_not_set") { cueError(); setState((s) => ({ ...s, error: PIN_MESSAGES[d.reason] })); return; }
    route(d);
  };

  const failMessage = (res, fallback) => STAFF_PIN_MESSAGES[res.data?.detail] || (typeof res.data?.detail === "string" && !/^[a-z_]+$/.test(res.data.detail) ? res.data.detail : fallback);
  const staffFail = (res) => { if (STAFF_PIN_MESSAGES[res.data?.detail]) dropUnlock(); };

  const submitPoints = async (amount) => {
    unlockAudio();
    setSubmitting(true);
    try {
      let res = await postJson(`/stamp/${token}/points`, { amount, another: !!state.pending?.another, ...creds() });
      if (res.ok && res.data.duplicate) res = await postJson(`/stamp/${token}/points`, { amount, another: true, ...creds() });
      if (!res.ok) { staffFail(res); cueError(); setPinError(failMessage(res, "Couldn't add points.")); setState({ kind: "pin" }); return; }
      keepUnlock(ctx?.org?.id, res.data);
      if (res.data.reward_unlocked) cueRedeem(); else cueSuccess();
      setState({ kind: "stamped", result: { ...res.data, change: (res.data.holder?.balance || 0) - (state.pending?.holder?.balance || 0) || 1 } });
    } finally { setSubmitting(false); }
  };

  const undo = async () => {
    if (!state.result?.idempotency_key) return;
    setUndoing(true);
    try {
      const res = await postJson(`/stamp/${token}/reverse`, { idempotency_key: state.result.idempotency_key, ...creds() });
      if (res.ok) { keepUnlock(ctx?.org?.id, res.data); await loadCtx(); setPinError(null); setState({ kind: "undone" }); return; }
      cueError();
      if (STAFF_PIN_MESSAGES[res.data?.detail]) { staffFail(res); setPinError(STAFF_PIN_MESSAGES[res.data.detail]); setState({ kind: "pin" }); }
      else toast.error(failMessage(res, "Couldn't undo — the 3-minute window may have passed."));
    } finally { setUndoing(false); }
  };

  const addAnother = () => {
    const r = state.result || {};
    if (r.program?.type === "points") { setState({ kind: "points_prompt", pending: { holder: r.holder, program: r.program, another: true } }); return; }
    earn(true);
  };

  const done = () => setState({ kind: "ready" });

  const screen = (() => {
    if (state.kind === "loading") return <div className="fixed inset-0 bg-[#0B0D10]" />;
    if (state.kind === "working") return <WorkingScreen ctx={ctx} />;
    if (state.kind === "not_found") return <MessageScreen org={null} testid="not-found-screen" title="Not a valid pass" body="This is not a valid BackPocket pass." />;
    if (state.kind === "blocked") return <MessageScreen org={ctx?.org} testid="stamp-blocked" title="Pass blocked" body="This pass has been blocked. The business owner can unblock it from the dashboard." />;
    if (state.kind === "viewer") return <ViewerScreen ctx={ctx} onStaff={() => { setPinError(null); setState({ kind: "pin" }); }} />;
    if (state.kind === "pin") return <StaffPinScreen ctx={ctx} onSubmit={submitStaffPin} error={pinError} submitting={submitting} onViewer={() => setState({ kind: "viewer" })} />;
    if (state.kind === "undone") return (
      <Shell org={ctx?.org} testid="undone-screen">
        <div className="text-center">
          <div className="h-20 w-20 rounded-full bg-white/[0.08] grid place-items-center mx-auto mb-6 border border-white/12"><RotateCcw size={30} className="text-white/85" /></div>
          <div className="font-display font-extrabold tracking-[-0.02em]" style={{ fontSize: "clamp(1.9rem, 7vw, 2.6rem)" }}>Undone</div>
          <div className="text-white/60 text-[14px] mt-3" data-testid="member-notice">Nothing was recorded on this pass.</div>
        </div>
        <div className="mt-8"><BalanceCard holder={ctx?.holder} program={ctx?.program} accent={brandOf(ctx?.org).accent} bg={brandOf(ctx?.org).bg} /></div>
        <button onClick={() => earn()} disabled={submitting} data-testid="undone-record-again" className="mt-6 w-full rounded-2xl h-12 font-semibold text-[14px] bg-white/[0.08] border border-white/12 hover:bg-white/[0.14] transition disabled:opacity-40">Record it again</button>
        <button onClick={done} data-testid="undone-done" className="mt-3 w-full rounded-2xl h-14 font-semibold text-[15px]" style={{ background: brandOf(ctx?.org).accent, color: brandOf(ctx?.org).bg }}>Done — next customer</button>
        <Footer />
      </Shell>
    );
    if (state.kind === "cooldown") return <CooldownScreen result={state.result} org={ctx?.org} onAddAnyway={() => earn(true)} onDone={done} adding={submitting} />;
    if (state.kind === "customer_pin") return <CustomerPinScreen pending={state.pending} org={ctx?.org} error={state.error} onPin={redeemWithCustomerPin} onEarnInstead={() => earn()} onCancel={done} submitting={submitting} />;
    if (state.kind === "points_prompt") return <PointsPrompt pending={state.pending} org={ctx?.org} onSubmit={submitPoints} onCancel={done} submitting={submitting} />;
    if (state.kind === "stamped") return <StampedScreen result={state.result} org={ctx?.org} token={token} onUndo={undo} undoing={undoing} onDone={done} onAddAnother={addAnother} adding={submitting} />;
    if (state.kind === "ready") return <ReadyScreen org={ctx?.org} />;
    return null;
  })();

  return (
    <>
      {screen}
      {state.kind !== "pin" && state.kind !== "viewer" && state.kind !== "loading" && <UnlockChip unlock={unlock} onLock={lock} />}
    </>
  );
}
